import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import StatusBadge from '../StatusBadge.jsx';

const PRICE_GRANULARITIES = ['low', 'medium', 'high', 'auto', 'dense'];

export default function SettingsTab({ publisher, onPublisherChange }) {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const s = await api.getPublisherSettings(publisher.id);
        if (!cancelled) {
          setSettings(s);
          setForm(toFormState(s));
        }
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiError ? err.message : 'Could not load settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [publisher.id]);

  function toFormState(s) {
    return {
      bidder_timeout_ms: s.bidder_timeout_ms ?? '',
      timeout_buffer_ms: s.timeout_buffer_ms ?? '',
      ad_server_currency: s.ad_server_currency ?? 'INR',
      price_granularity: s.price_granularity ?? 'medium',
      has_cmp: !!s.has_cmp,
      gdpr_scope: !!s.gdpr_scope,
      us_privacy_scope: !!s.us_privacy_scope,
      consent_timeout_ms: s.consent_timeout_ms ?? '',
    };
  }

  async function saveSettings(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        bidder_timeout_ms: numOrNull(form.bidder_timeout_ms),
        timeout_buffer_ms: numOrNull(form.timeout_buffer_ms),
        ad_server_currency: form.ad_server_currency,
        price_granularity: form.price_granularity,
        has_cmp: form.has_cmp,
        gdpr_scope: form.gdpr_scope,
        us_privacy_scope: form.us_privacy_scope,
        consent_timeout_ms: numOrNull(form.consent_timeout_ms),
      };
      const updated = await api.patchPublisherSettings(publisher.id, body);
      setSettings(updated);
      setForm(toFormState(updated));
      toast.success('Publisher settings saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(nextStatus) {
    setStatusBusy(true);
    try {
      const updated = await api.patchPublisher(publisher.id, { status: nextStatus });
      onPublisherChange(updated);
      toast.success(`Publisher marked ${nextStatus}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update status.');
    } finally {
      setStatusBusy(false);
    }
  }

  if (loading || !form) return <LoadingState label="Loading settings" />;

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Status</h2>
          <StatusBadge status={publisher.status} />
        </div>
        <p className="card__hint">
          Paused publishers stop receiving traffic without deleting their data. Archived publishers are hidden
          from active lists.
        </p>
        <div className="button-row">
          <button
            className="btn btn--ghost"
            disabled={statusBusy || publisher.status === 'active'}
            onClick={() => changeStatus('active')}
          >
            Activate
          </button>
          <button
            className="btn btn--ghost"
            disabled={statusBusy || publisher.status === 'paused'}
            onClick={() => changeStatus('paused')}
          >
            Pause
          </button>
          <button
            className="btn btn--danger-ghost"
            disabled={statusBusy || publisher.status === 'archived'}
            onClick={() => changeStatus('archived')}
          >
            Archive
          </button>
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Auction settings</h2>
        </div>
        <form className="form form--grid" onSubmit={saveSettings}>
          <label className="field">
            <span className="field__label">Bidder timeout (ms)</span>
            <input
              type="number"
              min="0"
              value={form.bidder_timeout_ms}
              onChange={(e) => setForm({ ...form, bidder_timeout_ms: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Timeout buffer (ms)</span>
            <input
              type="number"
              min="0"
              value={form.timeout_buffer_ms}
              onChange={(e) => setForm({ ...form, timeout_buffer_ms: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Ad server currency</span>
            <select
              value={form.ad_server_currency}
              onChange={(e) => setForm({ ...form, ad_server_currency: e.target.value })}
            >
              {['INR', 'USD'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Price granularity</span>
            <select
              value={form.price_granularity}
              onChange={(e) => setForm({ ...form, price_granularity: e.target.value })}
            >
              {PRICE_GRANULARITIES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Consent timeout (ms)</span>
            <input
              type="number"
              min="0"
              value={form.consent_timeout_ms}
              onChange={(e) => setForm({ ...form, consent_timeout_ms: e.target.value })}
            />
          </label>

          <div className="field form__actions--grid-span">
            <p className="card__hint">
              GDPR/TCF and GPP are only turned on for this publisher once a real CMP is confirmed
              installed on the page — leaving this off (the default) keeps auctions running exactly
              as they do today. Turning GDPR/GPP on without a real CMP installed cancels every
              auction on this site.
            </p>
          </div>
          <label className="field field--checkbox">
            <input
              type="checkbox"
              checked={form.has_cmp}
              onChange={(e) => {
                const has_cmp = e.target.checked;
                setForm({
                  ...form,
                  has_cmp,
                  gdpr_scope: has_cmp ? form.gdpr_scope : false,
                  us_privacy_scope: has_cmp ? form.us_privacy_scope : false,
                });
              }}
            />
            <span>This publisher has a real CMP installed on their page</span>
          </label>
          <label className={`field field--checkbox${form.has_cmp ? '' : ' field--checkbox-disabled'}`}>
            <input
              type="checkbox"
              disabled={!form.has_cmp}
              checked={form.gdpr_scope}
              onChange={(e) => setForm({ ...form, gdpr_scope: e.target.checked })}
            />
            <span>Apply GDPR / TCF consent signaling (EU traffic)</span>
          </label>
          <label className={`field field--checkbox${form.has_cmp ? '' : ' field--checkbox-disabled'}`}>
            <input
              type="checkbox"
              disabled={!form.has_cmp}
              checked={form.us_privacy_scope}
              onChange={(e) => setForm({ ...form, us_privacy_scope: e.target.checked })}
            />
            <span>Apply GPP consent signaling (US state privacy laws)</span>
          </label>

          <div className="form__actions form__actions--grid-span">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}
